"use client";

import { useState, useMemo, useEffect, Fragment } from "react";
import { FileSpreadsheet, Users, Loader2, Search, Check, User, UserMinus, Info, Sparkles, ChevronDown, Download, ArrowRightLeft, ShieldCheck, ArrowUpDown, FileText, ArrowLeft } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/app/_module/components/ui/tooltip";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import { useGetLeadsImportList } from "../../../leads/_modules/hooks/leads-import-queries";
import { leadAssignmentService } from "../services/service";
import ImportedLeadListDetails from "@/app/(authenticated)/leads/_modules/components/ImportedLeadListDetails";
import ImportLeadsListModal from "@/app/(authenticated)/leads/_modules/components/ImportLeadsListModal";
import { AgentAssignmentDropdown } from "./AgentAssignmentDropdown";
import { ClosedLeadsOption } from "./AssignmentActions";

interface ImportItem {
  id: string;
  name: string;
  fileName: string;
  status: string;
  totalRecords: number;
  processedRecords: number;
  createdAt: string;
  resultCounts?: {
    total: number;
    success: number;
    failed: number;
    pendingActivation: number;
    created: number;
  };
}

interface ListDistribution {
  listName: string;
  total: number;
  unassigned: number;
  agents: { id: string | null; name: string; count: number; householdMembers?: number; role?: string; agencyName?: string }[];
}

interface SelectImportListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssignmentComplete: () => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Normalize a stored phone (usually E.164 like +12143353701) to (xxx) xxx-xxxx.
const formatPhone = (raw?: string): string => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  return raw;
};

// Stable key for a (agent, list) row — used for the unassign spinner, the
// expand/collapse set, and the preview cache. The `::` delimiter avoids the
// concatenation collision where "a"+"bc" === "ab"+"c".
const rowKey = (agentId: string | null | undefined, listName: string) =>
  `${agentId || ""}::${listName}`;

// Split a list's agent distribution into the PRIMARY assignee and the OTHERS
// ("duplicates — already in another agent's leads, NOT the agent you're
// assigning to"). The label is defined RELATIVE TO THE TARGET AGENT, so the
// split must be too:
//   • When a target agent is selected → PRIMARY = that target (the agent you're
//     assigning to), OTHERS = every other agent holding leads from this list.
//     This was the bug: the old code picked PRIMARY = whoever held the most
//     leads, ignoring the target — so assigning "Moses july leads" to Abdullah
//     showed Moustafa as "Assigned to" and Abdullah (the target!) as a
//     "Duplicate", and near-identically-named lists looked like they shared the
//     wrong agents.
//   • With no target selected (just browsing) → fall back to the informational
//     majority view (the majority holder is the de-facto assignee).
// othersTotal is the true "another agent's leads" count for the headline.
const splitDistribution = (
  dist?: ListDistribution,
  targetAgentId?: string,
) => {
  const agents = (dist?.agents ?? []).filter((a) => a.count > 0);
  const none = { primary: null as ListDistribution["agents"][number] | null, others: [] as ListDistribution["agents"], othersTotal: 0 };
  if (agents.length === 0) return none;
  const sorted = [...agents].sort((a, b) => b.count - a.count);

  // Target-aware: the agent you're assigning TO is the assignee; everyone else
  // is genuinely "another agent's leads". If the target holds none of this list
  // yet, primary is null (all existing holders are the duplicates).
  if (targetAgentId) {
    const primary = sorted.find((a) => a.id === targetAgentId) || null;
    const others = sorted.filter((a) => a.id !== targetAgentId);
    return { primary, others, othersTotal: others.reduce((s, a) => s + a.count, 0) };
  }

  const total = dist?.total ?? sorted.reduce((s, a) => s + a.count, 0);
  const top = sorted[0];
  // No target: the top agent is the de-facto assignee only if they hold the BULK
  // (strict majority). Otherwise the few holders are just duplicates.
  if (top.count * 2 <= total) {
    return { primary: none.primary, others: sorted, othersTotal: sorted.reduce((s, a) => s + a.count, 0) };
  }
  const [primary, ...others] = sorted;
  return { primary, others, othersTotal: others.reduce((s, a) => s + a.count, 0) };
};

export const SelectImportListModal = ({
  isOpen,
  onClose,
  onAssignmentComplete,
}: SelectImportListModalProps) => {
  const [selectedImports, setSelectedImports] = useState<Set<string>>(new Set());
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  // Plain-English legend so any agent can read what every label means.
  const [showLegend, setShowLegend] = useState(false);
  // Which list's import details are open, if any. Opening this does NOT close
  // the assign modal — you look at the file, then carry on assigning.
  const [detailsImportId, setDetailsImportId] = useState<string | null>(null);

  const [isAssigning, setIsAssigning] = useState(false);
  // Per-list progress for the bar below the button. Assigning a run of 15k-lead
  // lists takes long enough that a bare spinner reads as a hung page — people
  // clicked again and then reported an error on work that had actually
  // succeeded. Granularity is per LIST because that is what the loop does: each
  // list is one backend call, so there is no honest sub-list percentage to show.
  const [assignProgress, setAssignProgress] = useState<{
    current: number; total: number; listName: string; leadsDone: number; leadsTotal: number;
  } | null>(null);
  const [cleanSlate, setCleanSlate] = useState(false);
  // Cross-agent override. Default FALSE: uploading a list must not quietly
  // empty a teammate's book. Reset on close so it can never persist into the
  // next assignment the owner does.
  const [moveFromOtherAgents, setMoveFromOtherAgents] = useState(false);
  const [unassigningAgent, setUnassigningAgent] = useState<string | null>(null);
  // Per-list unassign waits on a confirm that names how many sold/closed leads will be kept.
  const [pendingUnassign, setPendingUnassign] = useState<{ listName: string; agentId: string; agentName: string; leadIds: string[]; closedCount: number } | null>(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"name-asc" | "name-desc" | "newest" | "oldest" | "leads-desc" | "leads-asc">("newest");
  const [distributions, setDistributions] = useState<ListDistribution[]>([]);
  // Per-import "where did my rows go" breakdown (created vs skipped + reasons), so
  // an owner who uploads 700 rows and sees 590 leads understands the 110 gap
  // (duplicates, bad phones, household merges, missing fields).
  const [importBreakdowns, setImportBreakdowns] = useState<Record<string, {
    totalRows: number; created: number; totalSkipped: number;
    /** Created and not deleted since (missing from older backends). */
    live?: number; deleted?: number;
    skipped: { duplicates: number; invalidPhone: number; missing: number; other: number };
    householdMerged: number; displayedContacts: number;
  }>>({});
  // Per-import-list toggle to reveal agents holding only 1-2 leads from this list.
  // They clutter the breakdown, so they collapse by default; the real distributor
  // (the agent with hundreds/thousands) always renders.
  //
  // WHY THEY EXIST — this was assumed to be hand-reassignment ("why does Moustafa
  // show up with 1 lead in every CSV?") and collapsed to hide the noise. Actually
  // investigating it on 2026-08-18 gave a different answer: of 7 such leads across
  // Moe's lists, 5 were HOUSEHOLD MEMBERS legitimately pulled along so a family
  // stays with one agent, and 2 came from a placeholder phone (+18888888888) that
  // welded strangers into a household. Only the second kind was ever a defect, and
  // it is fixed at the source now.
  //
  // So hiding them was treating the symptom. The row is still collapsed (it IS
  // clutter), but the label no longer calls them "duplicates" and expanding one
  // names the list the household came from.
  const STRAY_AGENT_THRESHOLD = 3;
  const [revealStrayFor, setRevealStrayFor] = useState<Set<string>>(new Set());
  // Expand an agent row to preview WHICH of their leads overlap this list — so
  // owners see the actual duplicates by name + number, not just a count.
  const AGENT_LEADS_PREVIEW_LIMIT = 50;
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());
  const [agentLeadsCache, setAgentLeadsCache] = useState<Record<string, { id: string; name: string; phone: string; listName?: string; createdOn?: string; appearedIn?: { list: string; date: string }[]; householdFromList?: string }[]>>({});
  const [loadingLeadsKey, setLoadingLeadsKey] = useState<string | null>(null);
  // Bumped after every assign/unassign so the informational effects below
  // (which key on data that doesn't change when leads move) re-run.
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportingKey, setExportingKey] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: importListData, isLoading } = useGetLeadsImportList({
    refetchInterval: 30000,
  });

  // Fetch list distributions
  const fetchDistributions = async () => {
    try {
      const result: any = await handleApiCall(async () =>
        api.get({ url: getApiUrl('/lead-assignment/list-distribution'), auth: true })
      );
      // Handle multiple response shapes from handleApiCall
      const data = result?.data?.data || result?.data || result || [];
      setDistributions(Array.isArray(data) ? data : []);
    } catch {
      setDistributions([]);
    }
  };

  /**
   * Everything this modal must re-pull after an assign or unassign.
   *
   * WHY: the modal holds three independent caches, and only one of them used to
   * refresh. `distributions` was refetched, but the per-import breakdown (the
   * duplicate/stray FLAGS) is keyed on `[isOpen, breakdownIds]` — neither changes
   * when leads move, so the effect never re-ran and the flags kept showing the
   * pre-assignment state. `agentLeadsCache` is keyed per agent+list and is only
   * populated on expand, so an expanded row kept listing leads that had just been
   * moved away. Net effect for the user: "it says it's done but still shows the
   * old numbers unless I close the popup and come back in."
   *
   *   assign / unassign resolves
   *          │
   *          ├─ bump refreshKey ──► breakdown effect re-runs  (dupe flags)
   *          ├─ clear agentLeadsCache ──► expanded rows re-fetch on next open
   *          ├─ await fetchDistributions() ──► per-list agent counts
   *          └─ onAssignmentComplete() ──► parent page's own queries
   *
   * AWAITED, not fire-and-forget: the caller keeps its spinner up until this
   * resolves, so the button re-enables only once the numbers on screen are real.
   */
  const refreshAfterMutation = async () => {
    setAgentLeadsCache({});
    setRefreshKey((k) => k + 1);
    await fetchDistributions();
    onAssignmentComplete();
  };

  // Fetch on modal open
  useEffect(() => {
    if (!isOpen) return;
    fetchDistributions();
  }, [isOpen]);

  // Unassign all leads from a specific list + agent.
  //
  // Two layers of paging:
  //   1. GET /unassigned is paginated (5k per page) and walked until empty —
  //      previously a single ?limit=9999 call would silently miss any leads
  //      past the 9,999th. Imports are capped at 20k per upload, but stacked
  //      lists/imports could exceed that for one agent.
  //   2. POST /unassign is capped at 5k IDs per call — chunked so any size
  //      collected from #1 still goes through cleanly with a progress toast.
  const UNASSIGN_PAGE_SIZE = 5000;
  const UNASSIGN_CHUNK_SIZE = 5000;
  const handleUnassignFromList = async (listName: string, agentId: string, agentName: string, count: number) => {
    setUnassigningAgent(rowKey(agentId, listName));
    try {
      // Step 1 — walk all pages until we have every assigned lead ID.
      const leadIds: string[] = [];
      let page = 1;
      // Hard ceiling so a buggy server response can't loop forever — well above
      // realistic max (20k upload cap × small multiplier of stacked lists).
      const MAX_PAGES = 50;
      while (page <= MAX_PAGES) {
        const result: any = await handleApiCall(async () =>
          api.get({
            url: getApiUrl(`/lead-assignment/list-agent-leads?listName=${encodeURIComponent(listName)}&agentId=${agentId}&limit=${UNASSIGN_PAGE_SIZE}&page=${page}`),
            auth: true,
          })
        );
        const container: any = result?.data?.data ?? result?.data ?? result ?? {};
        const leads: any[] = Array.isArray(container) ? container : (container.items || container.leads || []);
        const pageIds: string[] = leads.map((l: any) => l.id).filter(Boolean);
        if (pageIds.length === 0) break;
        leadIds.push(...pageIds);
        if (pageIds.length < UNASSIGN_PAGE_SIZE) break;
        page++;
      }

      if (leadIds.length === 0) {
        toast({ title: "No leads found", description: "Could not find leads to unassign", variant: "destructive" });
        return;
      }

      // Stop here and ask: sold/closed leads stay with the agent unless the owner opts in.
      const preview = await leadAssignmentService.previewUnassign(leadIds).catch(() => ({ closedCount: 0 }));
      setIncludeClosed(false);
      setPendingUnassign({ listName, agentId, agentName, leadIds, closedCount: preview?.closedCount ?? 0 });
    } catch (error: any) {
      toast({ title: "Unassign failed", description: error?.message || "Something went wrong", variant: "destructive" });
    } finally {
      setUnassigningAgent(null);
    }
  };

  const confirmUnassignFromList = async () => {
    if (!pendingUnassign) return;
    const { listName, agentId, agentName, leadIds } = pendingUnassign;
    setPendingUnassign(null);
    setUnassigningAgent(rowKey(agentId, listName));
    try {
      // Step 2 — POST chunks of ≤5k to /unassign.
      let unassignedTotal = 0;
      let keptTotal = 0;
      const chunkCount = Math.ceil(leadIds.length / UNASSIGN_CHUNK_SIZE);

      for (let i = 0; i < leadIds.length; i += UNASSIGN_CHUNK_SIZE) {
        const chunk = leadIds.slice(i, i + UNASSIGN_CHUNK_SIZE);
        const chunkIndex = Math.floor(i / UNASSIGN_CHUNK_SIZE) + 1;

        if (chunkCount > 1) {
          toast({
            title: `Unassigning ${chunkIndex}/${chunkCount}…`,
            description: `Processing ${chunk.length} leads from "${listName}"`,
          });
        }

        const resp: any = await handleApiCall(async () =>
          api.post({
            url: getApiUrl('/lead-assignment/unassign'),
            body: { leadIds: chunk, includeClosed },
            auth: true,
          })
        );

        const body = resp?.data?.data ?? resp?.data ?? resp ?? {};
        const kept = Array.isArray(body.skippedClosedIds) ? body.skippedClosedIds.length : 0;
        keptTotal += kept;
        unassignedTotal += chunk.length - kept;
      }

      toast({
        title: "Leads unassigned",
        description: `${unassignedTotal} of ${leadIds.length} leads from "${listName}" unassigned from ${agentName}${keptTotal ? `; ${keptTotal} sold/closed stayed with them` : ""}`,
      });

      await refreshAfterMutation();
    } catch (error: any) {
      toast({
        title: "Unassign failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setUnassigningAgent(null);
    }
  };

  // Expand/collapse an agent row and lazily fetch the actual overlapping leads
  // (name + phone) the first time it's opened. Reuses the same listName+agent
  // filter the Unassign flow uses.
  const toggleAgentLeads = async (listName: string, agentId: string) => {
    const key = rowKey(agentId, listName);
    const wasExpanded = expandedAgents.has(key);
    setExpandedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (wasExpanded) return; // collapsing — nothing to fetch
    // Refetch when we have nothing cached OR a previous attempt cached an EMPTY
    // result (e.g. before the endpoint fix) — otherwise an empty cache would
    // stick and keep showing "No leads to show".
    if (agentLeadsCache[key] && agentLeadsCache[key].length > 0) return;
    setLoadingLeadsKey(key);
    try {
      const result: any = await handleApiCall(async () =>
        api.get({
          url: getApiUrl(`/lead-assignment/list-agent-leads?listName=${encodeURIComponent(listName)}&agentId=${agentId}&limit=${AGENT_LEADS_PREVIEW_LIMIT}&page=1`),
          auth: true,
        })
      );
      const container: any = result?.data?.data ?? result?.data ?? result ?? {};
      const leads: any[] = Array.isArray(container) ? container : (container.items || container.leads || []);
      const mapped = leads.map((l: any) => ({
        id: l.id,
        name: l.name || l.fullName || [l.firstName, l.lastName].filter(Boolean).join(" ") || "Unnamed lead",
        phone: formatPhone(l.phone || l.phoneNumber || l.mobile || ""),
        // Provenance — which upload created this lead + every list it appeared in
        listName: l.listName || undefined,
        createdOn: l.createdOn || undefined,
        appearedIn: Array.isArray(l.appearedIn) ? l.appearedIn : undefined,
        // Set when this lead was pulled in as a household member of ANOTHER list.
        householdFromList: l.householdFromList || undefined,
      }));
      setAgentLeadsCache((prev) => ({ ...prev, [key]: mapped }));
    } catch {
      setAgentLeadsCache((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingLeadsKey(null);
    }
  };

  // Export the FULL overlapping-lead list for an agent (name + phone) as a CSV,
  // walking every page so it isn't capped at the in-box preview limit.
  const handleExportAgentLeads = async (listName: string, agentId: string, agentName: string) => {
    const key = rowKey(agentId, listName);
    setExportingKey(key);
    try {
      const rows: { name: string; phone: string }[] = [];
      let page = 1;
      const MAX_PAGES = 50;
      while (page <= MAX_PAGES) {
        const result: any = await handleApiCall(async () =>
          api.get({
            url: getApiUrl(`/lead-assignment/list-agent-leads?listName=${encodeURIComponent(listName)}&agentId=${agentId}&limit=${UNASSIGN_PAGE_SIZE}&page=${page}`),
            auth: true,
          })
        );
        const container: any = result?.data?.data ?? result?.data ?? result ?? {};
        const leads: any[] = Array.isArray(container) ? container : (container.items || container.leads || []);
        if (leads.length === 0) break;
        for (const l of leads) {
          rows.push({
            name: l.name || l.fullName || [l.firstName, l.lastName].filter(Boolean).join(" ") || "Unnamed lead",
            phone: formatPhone(l.phone || l.phoneNumber || l.mobile || ""),
          });
        }
        if (leads.length < UNASSIGN_PAGE_SIZE) break;
        page++;
      }
      if (rows.length === 0) {
        toast({ title: "Nothing to export", description: "No leads found for this agent.", variant: "destructive" });
        return;
      }
      const esc = (v: string) => `"${(v || "").replace(/"/g, '""')}"`;
      const csv = ["Name,Phone", ...rows.map((r) => `${esc(r.name)},${esc(r.phone)}`)].join("\r\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const safe = (s: string) => (s || "list").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").slice(0, 60);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe(listName)}-${safe(agentName)}-overlapping-leads.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Exported", description: `${rows.length.toLocaleString()} lead${rows.length === 1 ? "" : "s"} exported to CSV.` });
    } catch (error: any) {
      toast({ title: "Export failed", description: error?.message || "Something went wrong", variant: "destructive" });
    } finally {
      setExportingKey(null);
    }
  };

  const importLists: ImportItem[] = useMemo(() => {
    const list = Array.isArray(importListData)
      ? importListData.filter((imp: ImportItem) => {
          const hasLeads = (imp.resultCounts?.success || 0) > 0 || imp.totalRecords > 0;
          const isUsable = imp.status === "completed" || imp.status === "ready_for_activation";
          return hasLeads && isUsable;
        })
      : [];

    const q = search.trim().toLowerCase();
    const filtered = !q
      ? list
      : list.filter(
          (imp) =>
            (imp.name || "").toLowerCase().includes(q) ||
            (imp.fileName || "").toLowerCase().includes(q)
        );
    // Sort by the user-chosen order so lists are easy to scan/find instead of
    // appearing in raw import order. Default is A–Z by displayed name.
    const displayName = (i: ImportItem) => (i.fileName || i.name || "");
    const listLeads = (i: ImportItem) => i.resultCounts?.success || i.totalRecords || 0;
    const listTime = (i: ImportItem) => new Date(i.createdAt as unknown as string).getTime() || 0;
    const sorted = [...filtered];
    switch (sortBy) {
      case "name-desc":
        sorted.sort((a, b) => displayName(b).localeCompare(displayName(a), undefined, { sensitivity: "base" }));
        break;
      case "newest":
        sorted.sort((a, b) => listTime(b) - listTime(a));
        break;
      case "oldest":
        sorted.sort((a, b) => listTime(a) - listTime(b));
        break;
      case "leads-desc":
        sorted.sort((a, b) => listLeads(b) - listLeads(a));
        break;
      case "leads-asc":
        sorted.sort((a, b) => listLeads(a) - listLeads(b));
        break;
      case "name-asc":
      default:
        sorted.sort((a, b) => displayName(a).localeCompare(displayName(b), undefined, { sensitivity: "base" }));
        break;
    }
    return sorted;
  }, [importListData, search, sortBy]);

  // Stable joined-id key for the breakdown fetch. `importLists` gets a NEW array
  // reference on every 30s poll (refetchInterval) and every search keystroke,
  // which re-fired the heavy import-breakdown query needlessly (the one that
  // "jammed the page for Sam"). Depending on this STRING instead means the
  // effect only re-runs when the actual set of ids changes — a poll that returns
  // the same imports produces a value-equal string and is a no-op.
  //
  // Cap at 40 (matches the server-side cap); prioritise imports that produced
  // leads so the rest fall back to the lead-count estimate.
  const breakdownIds = useMemo(() => {
    const lc = (i: ImportItem) => i.resultCounts?.success || i.totalRecords || 0;
    return [...importLists]
      .sort((a, b) => lc(b) - lc(a))
      .map((i) => i.id)
      .filter(Boolean)
      .slice(0, 40)
      .join(',');
  }, [importLists]);

  // Fetch the per-import "where did my rows go" breakdown (one batched call).
  useEffect(() => {
    if (!isOpen || !breakdownIds) return;
    let cancelled = false;
    (async () => {
      try {
        const result: any = await handleApiCall(async () =>
          api.get({ url: getApiUrl(`/lead-assignment/import-breakdown?importIds=${encodeURIComponent(breakdownIds)}`), auth: true })
        );
        const data = result?.data?.data || result?.data || result || {};
        if (!cancelled && data && typeof data === 'object') setImportBreakdowns(data);
      } catch {
        /* non-fatal — breakdown is informational only */
      }
    })();
    return () => { cancelled = true; };
    // refreshKey: leads moving does NOT change isOpen or breakdownIds, so without
    // it the duplicate/stray flags stay frozen at their pre-assignment values.
  }, [isOpen, breakdownIds, refreshKey]);

  // Match import to distribution data by name
  // Match import to distribution by list_name (import name is stored as list_name on leads)
  const getDistribution = (imp: ImportItem): ListDistribution | undefined => {
    const exact = distributions.find((d) =>
      d.listName === imp.name ||
      d.listName === imp.fileName ||
      d.listName === (imp.name || imp.fileName)
    );
    if (exact) return exact;
    // Fallback: case/whitespace-insensitive — imports renamed after upload or
    // CSVs with stray spaces otherwise show as "not assigned" forever.
    const norm = (s?: string | null) => (s || "").trim().toLowerCase();
    const a = norm(imp.name);
    const b = norm(imp.fileName);
    return distributions.find((d) => {
      const n = norm(d.listName);
      return (a && n === a) || (b && n === b);
    });
  };

  const selectedImportItems = useMemo(() =>
    importLists.filter((imp) => selectedImports.has(imp.id)),
    [importLists, selectedImports]
  );

  const handleToggleImport = (imp: ImportItem) => {
    setSelectedImports((prev) => {
      const next = new Set(prev);
      if (next.has(imp.id)) {
        next.delete(imp.id);
      } else {
        next.add(imp.id);
      }
      return next;
    });
  };

  const handleAssign = async () => {
    if (selectedImports.size === 0 || !selectedAgentId || isAssigning) return;

    setIsAssigning(true);
    let totalAssigned = 0;
    let totalSkipped = 0;
    // Leads that were with a DIFFERENT agent get moved to the target (the
    // owner's pick wins) — collect who they came from so the toast says so.
    let totalMoved = 0;
    const movedByAgent = new Map<string, number>();
    // Collect each per-import failure reason so the toast can tell the
    // user exactly what the backend rejected. Previously these were
    // swallowed into a generic "may already be assigned" — which lied to
    // users when the actual 403 was "agent is at capacity (697/1000)".
    const failureMessages: string[] = [];
    // Cross-agent leads deliberately NOT taken (2026-08-21 default).
    let totalProtected = 0;
    const protectedByAgent = new Map<string, number>();

    const totalLists = selectedImportItems.length;
    const leadsTotal = selectedImportItems.reduce((n, x: any) => n + (x?.leadCount ?? x?.count ?? 0), 0);
    let listsDone = 0;
    let leadsDone = 0;
    setAssignProgress({
      current: 1, total: totalLists,
      listName: (selectedImportItems[0] as any)?.name ?? '',
      leadsDone: 0, leadsTotal,
    });

    try {
      for (const imp of selectedImportItems) {
        setAssignProgress({
          current: listsDone + 1, total: totalLists,
          listName: (imp as any)?.name ?? '', leadsDone, leadsTotal,
        });
        // The backend takes a list 2,000 leads at a time (a 25k list in one call
        // outlasted Cloudflare's 100s); the progress bar moves per chunk.
        const listLeads = (imp as any)?.leadCount ?? (imp as any)?.count ?? 0;
        const listStart = leadsDone;
        let offset = 0;
        try {
          for (;;) {
            const result = await leadAssignmentService.assignFromImport(imp.id, selectedAgentId, cleanSlate, moveFromOtherAgents, offset);
            if (!result.success) {
              failureMessages.push(result.message || 'Assignment rejected');
              break;
            }
            totalAssigned += result.assignedCount;
            totalSkipped += result.skippedCount || 0;
            totalMoved += result.movedCount || 0;
            for (const m of result.movedLeads || []) {
              movedByAgent.set(m.movedFrom, (movedByAgent.get(m.movedFrom) || 0) + 1);
            }
            // Leads left with the agent who already had them. Tracked per holder
            // so the owner can see exactly whose book would be touched before
            // deciding to override.
            totalProtected += result.crossAgentSkippedCount || 0;
            for (const c of result.crossAgentSkipped || []) {
              protectedByAgent.set(c.heldBy, (protectedByAgent.get(c.heldBy) || 0) + 1);
            }
            if (result.nextOffset >= result.total) break;
            offset = result.nextOffset;
            leadsDone = listStart + Math.min(offset, listLeads || offset);
            setAssignProgress({
              current: listsDone + 1, total: totalLists,
              listName: (imp as any)?.name ?? '', leadsDone, leadsTotal,
            });
          }
        } catch (err: any) {
          // handleApiCall throws an Error whose .message is the backend's
          // response.data.message (e.g. "Cannot assign 751 leads. Agent has
          // 697 of 1000 leads. Only 303 remaining."). Surface it verbatim.
          // Chunks before the failing one stay assigned and are counted above.
          const msg = err?.message || 'Unknown error';
          failureMessages.push(msg);
        }
        listsDone += 1;
        leadsDone = listStart + listLeads;
      }

      // Work happened if leads were assigned OR moved over from another agent.
      // `totalMoved` used to be left out of this check, so a run that successfully
      // pulled leads across from other agents but assigned no NEW ones fell through
      // to the failure branch and told the owner "Assignment blocked" — for an
      // operation that had just done exactly what they asked. Seen in prod
      // 2026-08-17: "1 lead(s) will move from other agents to cfd33c09…".
      if (totalAssigned > 0 || totalMoved > 0) {
        const assignedLabel = totalAssigned > 0
          ? `${totalAssigned.toLocaleString()} leads from ${selectedImports.size} list${selectedImports.size > 1 ? 's' : ''} assigned`
          : `No new leads to assign`;
        const skippedSuffix = totalSkipped > 0 ? `, ${totalSkipped.toLocaleString()} skipped — already in this agent's book (matched across their whole book, not just this list)` : '';
        const movedSuffix = totalMoved > 0
          ? `, ${totalMoved.toLocaleString()} moved over (${Array.from(movedByAgent.entries()).map(([agent, n]) => `${n} from ${agent}`).join(', ')})`
          : '';
        const protectedSuffix = totalProtected > 0
          ? `, ${totalProtected.toLocaleString()} left with ${Array.from(protectedByAgent.entries()).map(([agent, n]) => `${agent} (${n})`).join(', ')}`
          : '';
        const failedSuffix = failureMessages.length > 0 ? ` (${failureMessages.length} list${failureMessages.length > 1 ? 's' : ''} failed)` : '';
        // Surface WHY the failed lists failed instead of swallowing it — e.g. an
        // agent hit capacity on list 2 while list 1 assigned fine. Dedupe so the
        // same reason across several lists isn't repeated.
        const uniqueReasons = Array.from(new Set(failureMessages.filter(Boolean)));
        const reasonLines = uniqueReasons.length > 0 ? `\n${uniqueReasons.map((r) => `• ${r}`).join('\n')}` : '';
        toast({
          title: totalAssigned > 0 ? 'Leads assigned' : 'Leads moved over',
          description: `${assignedLabel}${skippedSuffix}${movedSuffix}${protectedSuffix}${failedSuffix}${reasonLines}`,
        });
        // STAY OPEN. Owners assign several lists in a row — one per agent — and closing
        // after each one meant reopening the modal, re-searching and re-scrolling every
        // time. It also hid the result: the list you just assigned now shows its new
        // holder, which is the confirmation that the assignment worked.
        //
        // Clear the selection and refresh EVERYTHING the modal shows — distributions,
        // duplicate flags and any expanded agent rows. AWAITED so the spinner stays up
        // until the numbers on screen are real; the previous fire-and-forget refresh
        // re-enabled the button while the old counts were still rendered, which read as
        // "it says done but nothing changed".
        setSelectedImports(new Set());
        setSelectedAgentId("");
        setMoveFromOtherAgents(false);
        await refreshAfterMutation();
        return;
      }

      // Nothing got assigned — surface the actual reason(s) from the API.
      // Dedupe identical messages (e.g. all 4 lists hit the same agent
      // capacity error) so the toast doesn't repeat the same sentence 4×.
      const uniqueReasons = Array.from(new Set(failureMessages.filter(Boolean)));
      const noAssignReasons: string[] = [];
      if (totalSkipped > 0) {
        noAssignReasons.push(`${totalSkipped.toLocaleString()} already in this agent's book`);
      }
      if (totalProtected > 0) {
        noAssignReasons.push(
          `${totalProtected.toLocaleString()} already with ${Array.from(protectedByAgent.keys()).join(', ')}`,
        );
      }
      const noAssignSummary = noAssignReasons.length
        ? `No leads were assigned — ${noAssignReasons.join(', ')}.`
        : 'No leads were assigned.';
      const description = uniqueReasons.length === 0
        ? noAssignSummary
        : uniqueReasons.length === 1
          ? uniqueReasons[0]
          : uniqueReasons.map((r, i) => `${i + 1}. ${r}`).join('\n');

      // Red is reserved for ACTUAL failures — a genuine reason came back from the API.
      //
      // This used to also require `totalSkipped > 0` to count as informational, which
      // meant a clean no-op (nothing assigned, nothing skipped, nothing rejected) was
      // painted as "Assignment blocked". Combined with the missing `totalMoved` check
      // above, re-running an assignment that had already succeeded looked like a hard
      // failure — which is what sent an owner chasing a non-existent problem on
      // 2026-08-17 while his 11,703 leads were sitting correctly assigned the whole time.
      //
      // "The agent already has these leads" is the system working, not refusing.
      const hasRealFailure = uniqueReasons.length > 0;
      toast({
        title: hasRealFailure
          ? 'Assignment blocked'
          : totalSkipped > 0
            ? 'Already assigned — nothing to move'
            : 'Nothing to move',
        description,
        ...(hasRealFailure ? { variant: 'destructive' as const } : {}),
      });
    } catch (error: any) {
      toast({
        title: 'Assignment failed',
        description: error?.message || 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setIsAssigning(false);
      setAssignProgress(null);
    }
  };

  const handleClose = () => {
    // Refuse to close mid-operation. The busy bar asks the user to wait; this makes it
    // true rather than advisory. Closing here doesn't cancel anything server-side — the
    // assign completes regardless — but it tears down the component that was going to
    // refresh the counts, so the user comes back to stale numbers and concludes the
    // operation failed. That is the "it says done but still shows the old list" report.
    if (isAssigning || unassigningAgent) return;
    setSelectedImports(new Set());
    setSelectedAgentId("");
    setSearch("");
    setMoveFromOtherAgents(false);
    onClose();
  };

  const leadCount = (imp: ImportItem) =>
    imp.resultCounts?.success || imp.totalRecords || 0;

  // Check if selected agent already has all leads from ALL selected lists
  const agentAlreadyHasAll = useMemo(() => {
    if (selectedImports.size === 0 || !selectedAgentId) return false;
    return selectedImportItems.every((imp) => {
      const dist = getDistribution(imp);
      if (!dist) return false;
      const agentEntry = dist.agents.find((a) => a.id === selectedAgentId);
      return dist.unassigned === 0 && agentEntry && agentEntry.count >= dist.total;
    });
  }, [selectedImportItems, selectedAgentId, distributions]);

  /**
   * Who LOSES leads if this assignment goes ahead.
   *
   * assign-from-import deliberately MOVES leads that currently sit with another agent —
   * the owner's pick wins. That is intended, but it used to happen silently and only
   * showed up in the toast afterwards, so an owner assigning a list had no idea they were
   * pulling live prospects off someone else's book. Reported 2026-08-17: "every time I
   * assigned it pulled a lead from someone."
   *
   * Shown BEFORE the click so the move is a decision, not a surprise.
   */
  const leadsThatWillMove = useMemo(() => {
    if (selectedImports.size === 0 || !selectedAgentId) return { total: 0, from: [] as { name: string; count: number }[] };
    const byAgent = new Map<string, number>();
    for (const imp of selectedImportItems) {
      const dist = getDistribution(imp);
      if (!dist) continue;
      for (const a of dist.agents) {
        // Unassigned leads (id null) aren't a move — nobody loses them.
        if (!a.id || a.id === selectedAgentId) continue;
        byAgent.set(a.name, (byAgent.get(a.name) || 0) + a.count);
      }
    }
    const from = Array.from(byAgent.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((x, y) => y.count - x.count);
    return { total: from.reduce((s, f) => s + f.count, 0), from };
  }, [selectedImportItems, selectedAgentId, distributions]);

  const agentLeadsFromSelected = useMemo(() => {
    if (selectedImports.size === 0 || !selectedAgentId) return 0;
    return selectedImportItems.reduce((sum, imp) => {
      const dist = getDistribution(imp);
      if (!dist) return sum;
      return sum + (dist.agents.find((a) => a.id === selectedAgentId)?.count || 0);
    }, 0);
  }, [selectedImportItems, selectedAgentId, distributions]);

  // Cross-agent conflict: leads in the selected lists currently assigned to
  // someone OTHER than the chosen target. Hitting Assign will move those
  // leads off whoever has them now — surface it before they click.
  const crossAgentConflicts = useMemo(() => {
    if (selectedImports.size === 0 || !selectedAgentId) return [] as { agentId: string; name: string; count: number }[];
    const byAgent: Record<string, { agentId: string; name: string; count: number }> = {};
    for (const imp of selectedImportItems) {
      const dist = getDistribution(imp);
      if (!dist) continue;
      for (const agent of dist.agents) {
        if (!agent.id || agent.id === selectedAgentId) continue;
        if (!byAgent[agent.id]) {
          byAgent[agent.id] = { agentId: agent.id, name: agent.name, count: 0 };
        }
        byAgent[agent.id].count += agent.count;
      }
    }
    return Object.values(byAgent).sort((a, b) => b.count - a.count);
  }, [selectedImportItems, selectedAgentId, distributions]);

  const totalReassignCount = useMemo(
    () => crossAgentConflicts.reduce((sum, c) => sum + c.count, 0),
    [crossAgentConflicts],
  );

  const totalSelectedLeads = useMemo(() =>
    selectedImportItems.reduce((sum, imp) => sum + leadCount(imp), 0),
    [selectedImportItems]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <ResponsiveDialogContent size="xl" className="p-0 gap-0 overflow-hidden flex flex-col max-h-[90vh] max-h-[90dvh]">
        {/* ── IMPORT DETAILS OVERLAY ────────────────────────────────────────
            Covers this modal rather than replacing it, so Back returns you to
            the same scroll position and the same selection — assigning a run of
            lists is the whole point of this screen and losing your place after
            every peek at a file would defeat it. */}
        {detailsImportId && (
          <div className="absolute inset-0 z-50 flex flex-col bg-background overflow-hidden">
            <div className="flex items-center gap-2 border-b px-5 py-3 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={() => setDetailsImportId(null)}>
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back
              </Button>
              <span className="text-sm font-semibold">Import Details</span>
            </div>
            {/* min-w-0 + overflow-x-hidden: the details view carries a 4-column
                summary grid and long file names, and without a hard horizontal
                clip its content pushed past the dialog edge and got cut off
                rather than wrapping. */}
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain p-4">
              <div className="w-full min-w-0 max-w-full">
                <ImportedLeadListDetails leadListId={detailsImportId} />
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              Assign from Lead List
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-sm leading-relaxed">
                    <p className="font-medium mb-1">How This Works</p>
                    <p>Select one or more lead lists from your CSV imports, choose an agent, and assign all leads at once.</p>
                    <p className="mt-1.5 text-gray-400">Grayed out lists are fully assigned. Hover on an agent name to unassign their leads from that list.</p>
                    <p className="mt-1 text-gray-400">If the agent you&apos;re assigning to already has that person (same name, email, or phone), it&apos;s skipped — they won&apos;t get the same person twice. Leads sitting with another agent move to the agent you pick — the list follows your choice.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* The full Leads Lists manager, reused whole — it brings its own
                  trigger button, stats, filters, activation and delete. Sits
                  beside the title rather than pinned right, so it reads as part
                  of the heading instead of a stray control in the corner. */}
              <span className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <ImportLeadsListModal />
              </span>
            </DialogTitle>
            <DialogDescription className="sr-only">
              Select one or more previously imported lead lists, choose an agent, and assign the leads in bulk.
            </DialogDescription>
          </DialogHeader>

          {/* Search + Sort */}
          <div className="mt-3 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search lead lists..."
                className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:bg-white focus:border-primary focus:outline-none transition-colors"
              />
            </div>
            <div className="relative flex-shrink-0">
              <ArrowUpDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                title="Sort lead lists"
                className="h-9 pl-8 pr-7 text-sm border border-gray-200 rounded-md bg-gray-50 hover:bg-white focus:bg-white focus:border-primary focus:outline-none transition-colors appearance-none cursor-pointer"
              >
                <option value="name-asc">Name A–Z</option>
                <option value="name-desc">Name Z–A</option>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="leads-desc">Most leads</option>
                <option value="leads-asc">Fewest leads</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Plain-English legend — what every label here means, so any agent can read it */}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowLegend((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700"
            >
              <Info className="w-3 h-3" />
              What do these words mean?
              <ChevronDown className={`w-3 h-3 transition-transform ${showLegend ? '' : '-rotate-90'}`} />
            </button>
            {showLegend && (
              <div className="mt-1.5 rounded-md border border-gray-200 bg-gray-50 p-2.5 space-y-2 text-[11px] leading-relaxed">
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 inline-flex flex-shrink-0 px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 font-medium">Assigned</span>
                  <span className="text-gray-600">Now on this agent&apos;s list — ready for them to call.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 inline-flex flex-shrink-0 px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 font-medium">Already has them</span>
                  <span className="text-gray-600">Skipped — the <strong>agent you&apos;re assigning to already has this person</strong> in their leads. One agent can&apos;t have the same person twice. Nothing was lost.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 inline-flex flex-shrink-0 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium">With another agent</span>
                  <span className="text-gray-600">This person is <strong>currently in another agent&apos;s leads</strong> (they came in on an earlier list or import). Assigning this list <strong>leaves them with that agent</strong> unless you choose to take them.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 inline-flex flex-shrink-0 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200 font-medium">Duplicate (import)</span>
                  <span className="text-gray-600">You <strong>imported this person before</strong>, so they weren&apos;t added again. This only checks your own leads.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 inline-flex flex-shrink-0 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-medium">Household</span>
                  <span className="text-gray-600">Family members at the same home are <strong>grouped under one contact card</strong>, so the count looks lower.</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* List */}
        {/* flex-auto (basis:auto), NOT flex-1 (basis:0): the shell dialog is
            content-sized, and a basis-0 child contributes ZERO intrinsic
            height — WebKit collapsed this list to 0px ("shows nothing").
            max-h still caps it on both platforms. */}
        <div className="px-2 overflow-y-auto flex-auto min-h-0 max-h-[380px] [-webkit-overflow-scrolling:touch] overscroll-contain">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : importLists.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6">
              <FileSpreadsheet className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">
                {search ? "No lists match your search" : "No lead lists found"}
              </p>
              {!search && (
                <p className="text-xs text-gray-400 mt-1">Upload a CSV in the Leads page first</p>
              )}
            </div>
          ) : (
            <div className="space-y-0.5">
              {importLists.map((imp) => {
                const isSelected = selectedImports.has(imp.id);
                const dist = getDistribution(imp);
                // This import's own lead count — matches what "Assign" actually assigns
                // (assign-from-import works per-import via import_results, not by filename).
                // Live leads when the breakdown has them: the created count kept leads
                // deleted after upload (Zeinab's 800 file: 747 shown, 611 there).
                const liveBd = importBreakdowns[imp.id];
                const count = typeof liveBd?.live === "number" ? liveBd.live : leadCount(imp);
                const allAssigned = dist ? dist.unassigned === 0 : false;

                return (
                  <div
                    key={imp.id}
                    // NOTE: opacity-50 used to wrap the whole row when fully
                    // assigned. Because opacity cascades, the hover-revealed
                    // unassign button (line ~410, opacity-0 → group-hover:opacity-100)
                    // ended up at 50% even when hovered, making the row look
                    // "blocked out" and breaking the unassign UX. Now we only
                    // gray the checkbox + filename via inner classes; the
                    // agent breakdown stays at full opacity so the unassign
                    // button is fully clickable.
                    onClick={() => !allAssigned && handleToggleImport(imp)}
                    className={`w-full flex items-start gap-3 px-3 py-3 rounded-md text-left transition-all ${
                      allAssigned
                        ? "cursor-default"
                        : isSelected
                          ? "bg-primary/5 ring-1 ring-primary/30 cursor-pointer"
                          : "hover:bg-gray-50 cursor-pointer"
                    }`}
                  >
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      allAssigned
                        ? "border-green-400 bg-green-100 text-green-600"
                        : isSelected
                          ? "border-primary bg-primary text-white"
                          : "border-gray-300"
                    }`}>
                      {(allAssigned || isSelected) && (
                        <Check className="w-3.5 h-3.5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm truncate ${
                          allAssigned
                            ? "text-gray-400"
                            : isSelected
                              ? "font-medium text-gray-900"
                              : "text-gray-700"
                        }`}>
                          {imp.fileName || imp.name}
                        </p>
                        {imp.name && imp.fileName && imp.name !== imp.fileName && !imp.name.startsWith('Import ') && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded">
                            {imp.name}
                          </span>
                        )}
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(imp.createdAt)}</span>
                        {/* Opens the same Import Details view the Leads page uses:
                            row-by-row outcomes, the skip/error reasons, and the
                            raw CSV values. Deciding who gets a list often depends
                            on what is actually IN it — previously that meant
                            leaving here, going to Leads, and finding the import
                            again. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDetailsImportId(imp.id); }}
                          className="ml-auto flex-shrink-0 inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          title="View this file's rows, skips and errors"
                        >
                          <FileText className="w-3 h-3" />
                          View file
                        </button>
                      </div>

                      {/* Stats row — plain breakdown, no colored pills.
                          Total leads is the headline; if some are already
                          handed off to other agents, show that as quiet
                          subtext so users see the breakdown at a glance. */}
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {count.toLocaleString()} leads
                        </span>
                        {!!liveBd?.deleted && (
                          <span className="text-gray-400" title="Deleted since this file was uploaded. Not counted and not assigned.">
                            · {liveBd.deleted.toLocaleString()} deleted
                          </span>
                        )}
                        {(() => {
                          const { othersTotal } = splitDistribution(dist, selectedAgentId);
                          return othersTotal > 0 ? (
                            <span className="text-gray-400">
                              · {othersTotal.toLocaleString()} already with another agent
                            </span>
                          ) : null;
                        })()}
                      </div>

                      {/* "Where did my rows go?" — explains the gap between the file's
                          row count and the leads actually created (duplicates, bad
                          phones, household merges, missing fields) so an owner who
                          uploaded 700 and sees 590 understands why. */}
                      {(() => {
                        const bd = importBreakdowns[imp.id];
                        if (!bd || (bd.totalSkipped <= 0 && bd.householdMerged <= 0)) return null;
                        const skipParts: string[] = [];
                        if (bd.skipped.duplicates) skipParts.push(`${bd.skipped.duplicates.toLocaleString()} were duplicates already in your system`);
                        if (bd.skipped.invalidPhone) skipParts.push(`${bd.skipped.invalidPhone.toLocaleString()} had an invalid or missing phone number`);
                        if (bd.skipped.missing) skipParts.push(`${bd.skipped.missing.toLocaleString()} were missing required info`);
                        if (bd.skipped.other) skipParts.push(`${bd.skipped.other.toLocaleString()} were skipped for other reasons`);
                        return (
                          <div className="flex items-center gap-1 mt-0.5 text-[11px] text-amber-600">
                            <span>
                              {bd.created.toLocaleString()} of {bd.totalRows.toLocaleString()} rows became leads
                              {bd.totalSkipped > 0 ? ` · ${bd.totalSkipped.toLocaleString()} skipped` : ''}
                              {bd.householdMerged > 0 ? ` · ${bd.householdMerged.toLocaleString()} in households` : ''}
                            </span>
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-3 h-3 text-amber-500 hover:text-amber-700 cursor-help flex-shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                                  {skipParts.length > 0 && (
                                    <>
                                      <p className="font-medium mb-1">Why {bd.totalSkipped.toLocaleString()} of {bd.totalRows.toLocaleString()} rows weren&apos;t added as new leads:</p>
                                      <ul className="list-disc pl-4 space-y-0.5 mb-1.5">
                                        {skipParts.map((p, i) => <li key={i}>{p}</li>)}
                                      </ul>
                                    </>
                                  )}
                                  {bd.householdMerged > 0 && (
                                    <p>
                                      <span className="font-medium">{bd.householdMerged.toLocaleString()} {bd.householdMerged === 1 ? 'lead was' : 'leads were'} paired into a household</span> under a primary contact, so they show as one card. This agent will see about <span className="font-medium">{bd.displayedContacts.toLocaleString()}</span> contacts.
                                    </p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        );
                      })()}

                      {/* Agent breakdown — contacts in THIS list that already
                          belong to another agent. Usually these are DUPLICATES:
                          the same person was already in the system under another
                          agent (often from a different import), and re-importing
                          them here just re-tagged the existing record with this
                          list's name — it did NOT split this import between agents.
                          Each row has an Unassign button to pull them back to the
                          owner pool.
                          ─
                          By default we only show "real" distributions — agents with
                          STRAY_AGENT_THRESHOLD+ leads from this list. Single
                          leftover assignments (typically 1-2 leads someone
                          hand-reassigned weeks ago) get collapsed into a
                          "show stray reassignments" toggle. Without this the
                          breakdown gets dominated by phantom-looking 1-lead
                          entries that confuse owners ("why does X show up with
                          1 lead in every CSV?"). */}
                      {dist && dist.agents.length > 0 && (() => {
                        // PRIMARY = the agent this list was assigned to (most leads).
                        // OTHERS = duplicates that sit with someone else. We render
                        // them as two clearly-labeled sections so the assignee is
                        // never mistaken for "another agent".
                        const { primary, others } = splitDistribution(dist, selectedAgentId);
                        if (!primary && others.length === 0) return null;
                        const realOthers = others.filter(a => a.count >= STRAY_AGENT_THRESHOLD);
                        // A sub-threshold agent whose leads are ALL cross-list household
                        // members needs no toggle: those leads are already represented by
                        // the "N in households" figure in the list header, and surfacing
                        // them again as a separate "agents with a few leads" row is what
                        // made a fully-explained situation look like an anomaly. On
                        // "hadi leads august" both such agents were 100% household —
                        // 1 of 1 and 2 of 2 — so the row was pure noise.
                        // Anything NOT explained still gets a toggle; we hide the answered
                        // question, never an open one.
                        const strayOthers = others.filter(
                          (a) => a.count < STRAY_AGENT_THRESHOLD && (a.householdMembers ?? 0) < a.count,
                        );
                        const householdOnlyOthers = others.filter(
                          (a) => a.count < STRAY_AGENT_THRESHOLD && (a.householdMembers ?? 0) >= a.count,
                        );
                        const strayKey = imp.id;
                        const isStrayRevealed = revealStrayFor.has(strayKey);
                        const visibleOthers = isStrayRevealed ? [...realOthers, ...strayOthers] : realOthers;
                        // ⚠️ NO TRUNCATION. This used to be `.slice(0, 6)`, which
                        // silently dropped every agent past the sixth with nothing
                        // on screen to say so — on a widely-split list an owner
                        // simply could not see who held the rest of their leads.
                        // Show them all and let the container scroll instead.
                        const ordered = primary ? [primary, ...visibleOthers] : visibleOthers;
                        if (ordered.length === 0 && strayOthers.length === 0 && householdOnlyOthers.length === 0) return null;
                        // Scroll past ~6 rows rather than growing the card
                        // unboundedly — a list split across 20 agents would
                        // otherwise push everything else off the screen.
                        const scrolls = ordered.length > 6;
                        return (
                        <div
                          className={`mt-2 space-y-1${scrolls ? " max-h-[280px] overflow-y-auto pr-1" : ""}`}
                        >
                          {scrolls && (
                            <p className="sticky top-0 z-10 -mt-2 bg-white/95 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wide text-gray-400 backdrop-blur">
                              {ordered.length} agents hold leads from this list — scroll for all
                            </p>
                          )}
                          {ordered.map((agent, i) => {
                            const isPrimaryRow = !!primary && i === 0;
                            const isFirstDuplicate = primary ? i === 1 : i === 0;
                            const subtitle = [agent.role, agent.agencyName].filter(Boolean).join(' · ');
                            // Same list name the Unassign/expand/export handlers use, so
                            // the spinner + cache keys all match (prefers dist.listName —
                            // which may be the fileName when matched that way).
                            const listNameForAgent = dist?.listName || imp.name || imp.fileName;
                            const agentKey = rowKey(agent.id, listNameForAgent);
                            const isUnassigning = unassigningAgent === rowKey(agent.id, listNameForAgent);
                            const isExpanded = !!agent.id && expandedAgents.has(agentKey);
                            const previewLeads = agentLeadsCache[agentKey];
                            const isLoadingLeads = loadingLeadsKey === agentKey;
                            return (
                              <Fragment key={agent.id || i}>
                                {isPrimaryRow && (
                                  <p className="text-[11px] font-medium text-gray-600 px-0.5">Assigned to</p>
                                )}
                                {isFirstDuplicate && (
                                  <div className="px-0.5 pt-1">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-[11px] font-medium text-gray-600">Already with another agent (not the agent you&apos;re assigning to)</p>
                                      <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                                            <p><strong>Nobody assigned these to that agent from this list.</strong> These people were already in your system — they came in on an <strong>earlier</strong> upload that was assigned to that agent, and this new file happens to contain the same people. There&apos;s only <strong>one</strong> copy of each; nothing is duplicated. Assigning this list <strong>leaves them with that agent</strong> unless you tick &quot;Take them from the other agent anyway&quot;. Expand an agent to see each lead and which list first brought them in.</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </div>
                                    <p className="text-[10px] text-gray-400 leading-snug mt-0.5">You didn&apos;t assign these here — your file contains people who were <strong>already in your system from an earlier upload</strong>. Expand to see each lead and the list that first brought them in.</p>
                                  </div>
                                )}
                                <div
                                  className="rounded-md bg-gray-50/70 border border-gray-100 overflow-hidden"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                <div
                                  className={`flex items-center gap-2 px-2 py-1.5 ${agent.id ? 'cursor-pointer hover:bg-gray-100/70' : ''}`}
                                  onClick={() => { if (agent.id) toggleAgentLeads(listNameForAgent, agent.id); }}
                                >
                                  {agent.id ? (
                                    <ChevronDown className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                                  ) : (
                                    <span className="w-3 flex-shrink-0" />
                                  )}
                                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                    <User className="w-3 h-3" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-gray-800 truncate">{agent.name}</p>
                                    {/* Under the name, not beside it: beside it, a phone-width row
                                        squeezed the name to nothing. */}
                                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px]">
                                      {subtitle && <span className="text-gray-500 capitalize">{subtitle}</span>}
                                      {/* Was "Duplicate", which read as an error (Moe Ray, 2026-10-07).
                                          There is one copy; it came in on an earlier upload. Rows that
                                          are only household members say so on their own instead. */}
                                      {!isPrimaryRow && agent.count > (agent.householdMembers ?? 0) && (
                                        <span className="whitespace-nowrap rounded border border-amber-200 bg-amber-50 px-1.5 py-px font-medium text-amber-700">
                                          <span className="hidden sm:inline">From an earlier upload</span>
                                          <span className="sm:hidden">Earlier upload</span>
                                        </span>
                                      )}
                                      {/* Say how many of these arrived with a family rather
                                          than by assignment. An agent showing "1" under a list
                                          nobody gave them reads as a bug; "1 · 1 household
                                          member" reads as the system keeping a family together,
                                          which is what actually happened. */}
                                      {!!agent.householdMembers && agent.householdMembers > 0 && (
                                        <span className="whitespace-nowrap text-amber-700">
                                          {agent.householdMembers.toLocaleString()} household member{agent.householdMembers === 1 ? '' : 's'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-[11px] font-semibold text-gray-700 bg-white border border-gray-200 px-1.5 py-0.5 rounded flex-shrink-0">
                                    {agent.count.toLocaleString()}
                                  </span>
                                  {agent.id && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleUnassignFromList(listNameForAgent, agent.id!, agent.name, agent.count);
                                      }}
                                      disabled={!!unassigningAgent}
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                                      title={`Unassign ${agent.count} leads from ${agent.name}`}
                                      aria-label={`Unassign ${agent.count} leads from ${agent.name}`}
                                    >
                                      {isUnassigning ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <UserMinus className="w-3 h-3" />
                                      )}
                                      {/* The row itself reports progress — the icon alone
                                          left a 3–20s job looking like a dead click. */}
                                      <span>{isUnassigning ? "Removing…" : "Unassign"}</span>
                                    </button>
                                  )}
                                </div>
                                {isExpanded && (
                                  <div className="border-t border-gray-100 bg-white px-2.5 py-2">
                                    {isLoadingLeads ? (
                                      <div className="flex items-center gap-2 text-[11px] text-gray-500 py-1">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Loading leads…
                                      </div>
                                    ) : previewLeads && previewLeads.length > 0 ? (
                                      <>
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                          <p className="text-[10px] uppercase tracking-wide text-gray-400 truncate">{isPrimaryRow ? `Leads assigned to ${agent.name}` : `Leads currently with ${agent.name}`}</p>
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleExportAgentLeads(listNameForAgent, agent.id!, agent.name); }}
                                            disabled={exportingKey === agentKey}
                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                            title={`Export all ${agent.count.toLocaleString()} leads as CSV`}
                                          >
                                            {exportingKey === agentKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                            <span>Export CSV</span>
                                          </button>
                                        </div>
                                        <div className="space-y-0.5 max-h-44 overflow-y-auto">
                                          {previewLeads.map((lead) => {
                                            // Provenance line — answers "I never assigned this, why is it
                                            // with them?": lead with the OLDEST upload that contained this
                                            // person (appearedIn is date-ascending), then any re-deliveries.
                                            const history = (lead.appearedIn || []).filter((a) => a.list);
                                            const first = history[0];
                                            const resent = history.slice(1);
                                            const fmt = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                                            return (
                                              <div key={lead.id} className="py-0.5">
                                                <div className="flex items-center justify-between gap-3 text-[11px]">
                                                  <span className="text-gray-700 truncate">{lead.name}</span>
                                                  <span className="text-gray-500 tabular-nums flex-shrink-0">{lead.phone || '—'}</span>
                                                </div>
                                                {lead.householdFromList && (
                                                  <p className="text-[10px] text-amber-700 truncate leading-tight">
                                                    household member from &ldquo;{lead.householdFromList}&rdquo; — kept with this agent so the family isn&apos;t split
                                                  </p>
                                                )}
                                                {(first || lead.listName) && (
                                                  <p className="text-[10px] text-gray-400 truncate leading-tight">
                                                    {first
                                                      ? <>first came in on &ldquo;{first.list}&rdquo;{first.date ? ` · ${fmt(first.date)}` : ''}</>
                                                      : <>from &ldquo;{lead.listName}&rdquo;{lead.createdOn ? ` · ${fmt(lead.createdOn)}` : ''}</>}
                                                    {resent.length > 0 && (
                                                      <> · re-sent in {resent.map((a) => `“${a.list}”`).join(', ')}</>
                                                    )}
                                                  </p>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {agent.count > previewLeads.length && (
                                          <p className="text-[10px] text-gray-400 mt-1.5">+{(agent.count - previewLeads.length).toLocaleString()} more — use Export CSV for the full list</p>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-[11px] text-gray-400 py-1">No leads to show.</p>
                                    )}
                                  </div>
                                )}
                                </div>
                              </Fragment>
                            );
                          })}
                          {/* No "+N more agents" — every agent renders now and
                              the container scrolls. Counting a slice that no
                              longer exists would just be wrong. */}
                          {strayOthers.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRevealStrayFor((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(strayKey)) next.delete(strayKey);
                                  else next.add(strayKey);
                                  return next;
                                });
                              }}
                              className="text-[11px] text-gray-500 hover:text-gray-700 px-2 py-0.5 underline-offset-2 hover:underline focus:outline-none"
                            >
                              {/* NOT "duplicates". Investigated on 2026-08-18: of the leads
                                  sitting on these minority agents, the overwhelming majority
                                  are HOUSEHOLD MEMBERS — a bulk assign deliberately pulls a
                                  lead's family along so they land on one agent, and when the
                                  household spans two import files the lead surfaces under a
                                  list its agent was never assigned. Calling that a duplicate
                                  told the owner he had a data problem when the system was
                                  working correctly, and sent him chasing it repeatedly.
                                  Expanding the row now names the list the household came from. */}
                              {isStrayRevealed
                                ? `Hide ${strayOthers.length} agent${strayOthers.length === 1 ? '' : 's'} holding 1–${STRAY_AGENT_THRESHOLD - 1} leads from this list`
                                : `Show ${strayOthers.length} agent${strayOthers.length === 1 ? '' : 's'} holding 1–${STRAY_AGENT_THRESHOLD - 1} leads from this list (usually household members)`}
                            </button>
                          )}
                        </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t bg-gray-50/50 flex-shrink-0">
          {agentAlreadyHasAll && selectedImports.size > 0 && (
            <p className="text-xs text-red-600 font-medium mb-2">
              This agent already has all leads from the selected list{selectedImports.size > 1 ? 's' : ''}.
            </p>
          )}
          {/* LEADS HELD BY OTHER AGENTS.
              Until 2026-08-21 assign-from-import always MOVED these ("the owner's
              pick wins", 2026-07-22). In practice one agent uploading a vendor list
              silently emptied part of a teammate's book — Ricky's upload pulled
              leads out of Anersi's CRM with no warning on the losing side.
              They are now KEPT with their current agent by default, and taking them
              is an explicit checkbox rather than a side-effect of uploading. */}
          {leadsThatWillMove.total > 0 && (
            <div className={`mb-2 px-2.5 py-2 rounded-md border ${moveFromOtherAgents ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-300"}`}>
              <p className={`text-xs font-semibold flex items-center gap-1.5 ${moveFromOtherAgents ? "text-amber-900" : "text-slate-800"}`}>
                <ArrowRightLeft className="w-3.5 h-3.5 flex-shrink-0" />
                {moveFromOtherAgents
                  ? `This will TAKE ${leadsThatWillMove.total.toLocaleString()} lead${leadsThatWillMove.total > 1 ? "s" : ""} away from ${leadsThatWillMove.from.length === 1 ? "another agent" : `${leadsThatWillMove.from.length} other agents`}`
                  : `${leadsThatWillMove.total.toLocaleString()} lead${leadsThatWillMove.total > 1 ? "s" : ""} will stay with ${leadsThatWillMove.from.length === 1 ? "their current agent" : `their current agents`}`}
              </p>
              {/* Every affected agent is listed — this is the confirmation
                  that leads are about to LEAVE someone's book, so hiding names
                  behind "+N more" is exactly the wrong place to summarise. */}
              <ul className={`mt-1 space-y-0.5${leadsThatWillMove.from.length > 6 ? " max-h-[140px] overflow-y-auto pr-1" : ""}`}>
                {leadsThatWillMove.from.map((f) => (
                  <li key={f.name} className="text-[11px] text-amber-900/90">
                    <span className="font-semibold">{f.count.toLocaleString()}</span> from {f.name}
                  </li>
                ))}
              </ul>
              <p className={`text-[11px] mt-1 leading-snug ${moveFromOtherAgents ? "text-amber-800/90" : "text-slate-600"}`}>
                {moveFromOtherAgents
                  ? "Those leads leave their book and move to the agent you pick."
                  : "They are skipped, not re-assigned — the agent you pick gets the rest of the list."}{" "}
                If a name looks wrong here, it&apos;s usually one recycled phone number shared by
                two different people — check before assigning.
              </p>
              <label className="mt-2 flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 accent-amber-600"
                  checked={moveFromOtherAgents}
                  onChange={(e) => setMoveFromOtherAgents(e.target.checked)}
                />
                <span className="text-[11px] leading-snug text-slate-700">
                  Take them from the other agent{leadsThatWillMove.from.length > 1 ? "s" : ""} anyway
                  <span className="block text-[10px] text-slate-500">
                    Only do this if you mean to reassign their book. It cannot be undone in bulk.
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Duplicate-safe widget — explains the assign-time guard so agents stop
              thinking an import "split" their leads. The count is what we can see
              up-front (dupes from the selected list already on this agent); the
              guarantee below covers the rest (phone/email match at assign time). */}
          {selectedAgentId && !agentAlreadyHasAll && (
            <div className="mb-2 px-2.5 py-2 rounded-md bg-blue-50/70 border border-blue-200">
              <p className="text-xs text-blue-900 font-semibold flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
                Duplicate-safe assignment
              </p>
              {agentLeadsFromSelected > 0 && (
                <p className="text-[11px] text-blue-800 mt-0.5">
                  <span className="font-semibold">{agentLeadsFromSelected.toLocaleString()}</span> of these are already on this agent&apos;s leads — they&apos;ll be skipped.
                </p>
              )}
              <p className="text-[11px] text-blue-700/90 mt-0.5 leading-snug">
                At assign we match every lead by <strong>phone &amp; email</strong> against this agent&apos;s <strong>whole book — not just this list</strong>. Anything they already have (assigned or unassigned, this list or any other) is skipped automatically. They never get the same person twice.
              </p>
            </div>
          )}
          {/* Cross-agent move heads-up — the owner's pick wins: assigning this
              list delivers the WHOLE list to the chosen agent, including leads
              currently sitting with someone else. Say who they're coming from
              so there's no surprise. */}
          {totalReassignCount > 0 && !agentAlreadyHasAll && (
            <div className="mb-2 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-800 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                <span>
                  {totalReassignCount.toLocaleString()} lead{totalReassignCount === 1 ? '' : 's'} in this list {totalReassignCount === 1 ? 'is' : 'are'} currently with{' '}
                  {crossAgentConflicts.slice(0, 2).map((c, i) => (
                    <span key={c.agentId}>
                      {i > 0 ? ', ' : ''}
                      <span className="font-bold">{c.name}</span> ({c.count})
                    </span>
                  ))}
                  {crossAgentConflicts.length > 2 && <> and {crossAgentConflicts.length - 2} other{crossAgentConflicts.length - 2 === 1 ? '' : 's'}</>}
                  {' '}— assigning will <strong>move them to the agent you pick</strong>.
                </span>
              </p>
            </div>
          )}
          <div className="assign-footer flex items-center gap-3">
            <div className="flex-1">
              <AgentAssignmentDropdown
                value={selectedAgentId}
                onValueChange={setSelectedAgentId}
                placeholder="Select agent"
                disabled={isAssigning}
              />
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => setCleanSlate(!cleanSlate)}
                className={`clean-slate-toggle relative w-7 h-4 rounded-full transition-colors ${cleanSlate ? "bg-primary" : "bg-gray-300"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${cleanSlate ? "translate-x-3" : ""}`} />
              </button>
              <Sparkles className={`w-3 h-3 ${cleanSlate ? "text-primary" : "text-gray-400"}`} />
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    <p className="font-medium">Fresh Lead</p>
                    <p>The agent sees a clean lead — no previous notes, calls, or activity. Nothing is deleted.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Button
              onClick={handleAssign}
              disabled={selectedImports.size === 0 || !selectedAgentId || isAssigning || agentAlreadyHasAll}
              className="px-5"
            >
              {isAssigning ? (
                // Say what is happening, not just that something is. A bare spinner on a
                // job that can run 20s on a 15k list reads as a hung button, which is why
                // people clicked again and then reported an error on work that succeeded.
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Assigning{totalSelectedLeads > 0 ? ` ${totalSelectedLeads.toLocaleString()} leads` : ""}…
                </span>
              ) : (
                <>Assign{selectedImports.size > 0 ? ` ${totalSelectedLeads.toLocaleString()} leads` : ""}</>
              )}
            </Button>
          </div>

          {/* Progress. Shown only while a run is in flight; the label names the
              list currently being processed so a long job is legible, not just busy. */}
          {isAssigning && assignProgress && assignProgress.total > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span className="truncate pr-3">
                  List {Math.min(assignProgress.current, assignProgress.total)} of {assignProgress.total}
                  {assignProgress.listName ? ` — ${assignProgress.listName}` : ""}
                </span>
                <span className="tabular-nums shrink-0">
                  {assignProgress.leadsTotal > 0
                    ? `${assignProgress.leadsDone.toLocaleString()} / ${assignProgress.leadsTotal.toLocaleString()} leads`
                    : `${Math.round((assignProgress.current - 1) / assignProgress.total * 100)}%`}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-blue-600 transition-all duration-300"
                  style={{
                    width: `${Math.max(4, Math.round(((assignProgress.current - 1) / assignProgress.total) * 100))}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </ResponsiveDialogContent>
      {/* Confirm for the per-list Unassign: says what stays, and needs an explicit tick to take sold leads too. */}
      <Dialog open={!!pendingUnassign} onOpenChange={(open) => { if (!open) setPendingUnassign(null); }}>
        <ResponsiveDialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unassign {pendingUnassign?.leadIds.length ?? 0} leads from {pendingUnassign?.agentName}?</DialogTitle>
            <DialogDescription>
              They go back to the unassigned pool.
            </DialogDescription>
          </DialogHeader>
          {!!pendingUnassign?.closedCount && (
            <ClosedLeadsOption
              count={pendingUnassign.closedCount}
              all={pendingUnassign.closedCount >= pendingUnassign.leadIds.length}
              checked={includeClosed}
              onChange={setIncludeClosed}
            />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPendingUnassign(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!!pendingUnassign?.closedCount && pendingUnassign.closedCount >= pendingUnassign.leadIds.length && !includeClosed}
              onClick={() => void confirmUnassignFromList()}
            >
              Unassign
            </Button>
          </div>
        </ResponsiveDialogContent>
      </Dialog>
    </Dialog>
  );
};
